import { useMemo, useState } from 'react';
import { useTabState } from '../lib/tabParam';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { api, ApiClientError } from '../lib/api';
import { downloadDocument } from '../lib/downloadDocument';
import { latestAttr, registryOptions, humanizeToken, type AttrMap } from '../lib/inlineAttribution';
import { Badge, Button, IconArchive } from '../atoms';
import type { SelectOption } from '../atoms';
import { Tabs, DataTable, StatCard, LifecycleBar, CreateEntityModal, EntityActivityFeed, CustomFieldsPanel, EntityNotesPanel, DocumentUploadModal, ListToolbar, Pagination, FollowButton, LastUpdated, InlineEditCard, useToast } from '../organisms';
import { EntityHeader } from '../shell';
import { useEntityPermission } from '../lib/usePermissions';
import {
  CalendarEvent, UserLite,
  metaFor, EventModal, todayIso, userLabel, eventTitle,
} from './CalendarShared';
import { formatDate, formatDateTime, formatEventDate, formatEventTime } from '../lib/orgTimezone';

/**
 * ClientDetail — /clients/:id, the BUSINESS profile.
 *
 * The most-used page in the product: everything a case manager needs about one
 * business, laid out along the Supplier Journey (Assess → Connect → Develop →
 * Validate → Procure).
 *
 * EntityHeader (name + status + readiness/journey KPIs) above a Tabs panel:
 * Overview · Assessments · Referrals · Programs · Sessions · People · Notes ·
 * Calendar · Activity · Custom fields · Documents.
 *
 * The DB table is still `clients` (relabelled, not renamed — it already owns
 * contacts, documents, notes, follows and custom fields, and forking that wiring
 * for a rename would buy nothing), so every route below stays /api/clients/*.
 */

interface Business {
  id: string;
  name: string;
  short_name: string | null;
  status: 'active' | 'inactive' | 'in_setup' | 'historical';
  /** Account type (org-settings registry). */
  type: string | null;
  /** Industry (org-settings registry). */
  category: string | null;
  website_url: string | null;
  fax: string | null;
  address_line1: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  parent_client_id: string | null;
  client_manager_user_id: string | null;
  admin_associate_user_id: string | null;
  updated_at?: string | null;
  updated_by?: string | null;
}

/** A person at this business — contacts JOINed through contact_list. */
interface Person {
  id: string;
  first_name: string | null;
  last_name: string | null;
  search_name: string | null;
  company_name: string | null;
  title: string | null;
  status: string | null;
  /** contact_list.role — what this person does AT the business. */
  role: string | null;
}

/** One run of an instrument against this business (assessments + its template). */
interface AssessmentRow {
  id: string;
  sequence: string | null;
  status: string | null;
  composite_score: number | string | null;
  confidence: number | string | null;
  submitted_at: string | null;
  scored_at: string | null;
  instrument: string | null;
  template_version: number | null;
}

/** A hand-off to an ecosystem partner, for one capability the business lacks. */
interface ReferralRow {
  id: string;
  status: string | null;
  outcome: string | null;
  opened_date: string | null;
  closed_date: string | null;
  capability: string | null;
  partner: string | null;
}

/** This business's place in a development program (and its cohort, when set). */
interface EnrollmentRow {
  id: string;
  stage: string | null;
  role: string | null;
  application_date: string | null;
  start_date: string | null;
  end_date: string | null;
  attendance_rate: number | string | null;
  program: string | null;
  cohort: string | null;
}

/** A coaching session / consultation delivered to this business. */
interface SessionRow {
  id: string;
  kind: string | null;
  title: string | null;
  service: string | null;
  status: string | null;
  starts_at: string | null;
  mode: string | null;
  location: string | null;
}

/**
 * The single BFF payload GET /api/views/client/:id returns — the page's whole
 * initial (on-mount) read collapsed into one response. Each slice is gated on
 * its OWN entity server-side, so a role that may see the business but not its
 * assessments gets [] for that slice rather than a 403 for the page.
 */
interface BusinessView {
  client: Business;
  contacts: Person[];
  assessments: AssessmentRow[];
  referrals: ReferralRow[];
  enrollments: EnrollmentRow[];
  sessions: SessionRow[];
  counts: {
    contacts: number;
    assessments: number;
    referrals: number;
    enrollments: number;
    sessions: number;
  };
}

const statusVariant: Record<Business['status'], 'success' | 'warning' | 'neutral' | 'info'> = {
  active: 'success', inactive: 'neutral', in_setup: 'warning', historical: 'info',
};
const statusLabel: Record<Business['status'], string> = {
  active: 'Active', inactive: 'Inactive', in_setup: 'In Setup', historical: 'Historical',
};

/** A readiness score is NUMERIC in PG; render one decimal, never a fake unit. */
const scoreText = (v: number | string | null | undefined): string => {
  if (v == null || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(1) : '—';
};

/** A free-text status from the migrated org ('New', 'Completed') or a token. */
const tokenText = (v: string | null | undefined): string => (v ? humanizeToken(v) : '—');

export function ClientDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useTabState(
    ['overview', 'assessments', 'referrals', 'programs', 'sessions', 'people', 'notes', 'calendar', 'activity', 'custom', 'documents'] as const,
    'overview',
  );
  const [editOpen, setEditOpen] = useState(false);

  // BFF: one round-trip for the page's whole initial payload (the business record,
  // its people, and its journey — assessments, referrals, enrollments, sessions).
  // The per-tab lazy queries (Calendar/Documents/Activity/Custom/Notes) STAY lazy.
  const viewQuery = useQuery<BusinessView>({
    queryKey: ['client-view', id],
    queryFn: ({ signal }) => api.get<BusinessView>(`/api/views/client/${id}`, signal),
    enabled: !!id,
  });

  // Staff directory to resolve the case-manager / program-associate user ids →
  // names. JOINs contacts server-side, so first/last are present when set.
  const staffQuery = useQuery<Array<{ id: string; email: string; first_name: string | null; last_name: string | null }>>({
    queryKey: ['users-list'],
    queryFn: ({ signal }) => api.get('/api/users-list', signal),
    staleTime: 5 * 60_000,
  });

  if (viewQuery.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)' }}>Loading business…</p>;
  }

  if (viewQuery.error) {
    const notFound = viewQuery.error instanceof ApiClientError && viewQuery.error.status === 404;
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: notFound ? 'var(--color-card)' : 'var(--color-status-red-bg)',
        border: '1px solid ' + (notFound ? 'var(--color-border)' : 'var(--color-status-red)'),
        color: notFound ? 'var(--color-ink)' : 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        <h2 style={{ margin: '0 0 var(--sp-2)' }}>{notFound ? 'Business not found' : 'Failed to load business'}</h2>
        <p style={{ margin: 0 }}>{(viewQuery.error as Error).message}</p>
        <p style={{ marginTop: 'var(--sp-3)' }}>
          <Button variant="secondary" size="sm" onClick={() => navigate('/clients')}>
            Back to Businesses
          </Button>
        </p>
      </div>
    );
  }

  if (!viewQuery.data) return null;

  const client = viewQuery.data.client;
  const staffName = (uid: string | null): string => {
    if (!uid) return '—';
    const u = staffQuery.data?.find((x) => x.id === uid);
    if (!u) return '—';
    return [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || '—';
  };
  const staffOptions = (staffQuery.data ?? []).map((u) => ({
    value: u.id,
    label: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || u.id,
  }));

  const contacts = viewQuery.data.contacts ?? [];
  const assessments = viewQuery.data.assessments ?? [];
  const referrals = viewQuery.data.referrals ?? [];
  const enrollments = viewQuery.data.enrollments ?? [];
  const sessions = viewQuery.data.sessions ?? [];

  // Header + tab counts come straight off the view payload's precomputed counts
  // (identical to the array lengths — the BFF derives them from the same rows).
  const counts = viewQuery.data.counts;
  const contactCount = counts?.contacts ?? contacts.length;
  const assessmentCount = counts?.assessments ?? assessments.length;
  const referralCount = counts?.referrals ?? referrals.length;
  const enrollmentCount = counts?.enrollments ?? enrollments.length;
  const sessionCount = counts?.sessions ?? sessions.length;

  // The most recent scored run — the number a case manager and a buyer both ask
  // for. The BFF orders assessments by submitted_at DESC, so [0] is the latest.
  const latest = assessments[0] ?? null;
  const openReferrals = referrals.filter((r) => !r.closed_date).length;
  const programNames = new Set(enrollments.map((e) => e.program).filter(Boolean));

  return (
    <>
      <EntityHeader
        prefix={client.short_name && client.short_name !== client.name ? client.short_name : undefined}
        title={client.name}
        subtitle={
          <>
            {[client.category, client.type, [client.city, client.state].filter(Boolean).join(', ')]
              .filter(Boolean).join(' · ') || null}
            <LastUpdated updatedBy={client.updated_by} updatedAt={client.updated_at} />
          </>
        }
        statusPills={<Badge variant={statusVariant[client.status]} dot>{statusLabel[client.status]}</Badge>}
        actions={
          <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center', flexWrap: 'wrap' }}>
            <Button variant="secondary" size="sm" onClick={() => setEditOpen(true)}>
              Edit all fields
            </Button>
            <FollowButton entityType="client" entityId={client.id} />
          </div>
        }
        kpis={
          <>
            <StatCard
              onClick={() => setTab('assessments')}
              style={{ cursor: 'pointer' }}
              label="Readiness score"
              value={latest ? scoreText(latest.composite_score) : '—'}
              caption={latest
                ? [latest.instrument, latest.template_version != null ? `v${latest.template_version}` : null, tokenText(latest.sequence)]
                  .filter(Boolean).join(' · ')
                : 'Not assessed yet'}
              accent="hunter"
            />
            <StatCard
              onClick={() => setTab('referrals')}
              style={{ cursor: 'pointer' }}
              label="Referrals"
              value={String(referralCount)}
              caption={referralCount === 0 ? 'none opened' : `${openReferrals} still open`}
              accent="info"
            />
            <StatCard
              onClick={() => setTab('programs')}
              style={{ cursor: 'pointer' }}
              label="Programs"
              value={String(enrollmentCount)}
              caption={programNames.size === 1
                ? [...programNames][0] as string
                : `${programNames.size} program${programNames.size === 1 ? '' : 's'}`}
              accent="green"
            />
            <StatCard
              onClick={() => setTab('sessions')}
              style={{ cursor: 'pointer' }}
              label="Sessions"
              value={String(sessionCount)}
              caption="coaching and consultations"
              accent="amber"
            />
            <StatCard
              onClick={() => setTab('people')}
              style={{ cursor: 'pointer' }}
              label="People" value={String(contactCount)} caption="on file at this business" accent="info"
            />
          </>
        }
      />

      <Tabs
        activeValue={tab}
        onChange={(v) => setTab(v as typeof tab)}
        items={[
          { value: 'overview', label: 'Overview' },
          { value: 'assessments', label: 'Assessments', count: assessmentCount },
          { value: 'referrals', label: 'Referrals', count: referralCount },
          { value: 'programs', label: 'Programs', count: enrollmentCount },
          { value: 'sessions', label: 'Sessions', count: sessionCount },
          { value: 'people', label: 'People', count: contactCount },
          { value: 'notes', label: 'Notes' },
          { value: 'calendar', label: 'Calendar' },
          { value: 'activity', label: 'Activity' },
          { value: 'custom', label: 'Custom fields' },
          { value: 'documents', label: 'Documents' },
        ]}
      />

      <div style={{ marginTop: 'var(--sp-5)' }}>
        {tab === 'overview' && (
          <OverviewTab
            client={client}
            assessments={assessments}
            referrals={referrals}
            enrollments={enrollments}
            staffOptions={staffOptions}
            staffName={staffName}
          />
        )}
        {tab === 'assessments' && <AssessmentsTab assessments={assessments} onRowClick={(a) => navigate(`/assessments/${a.id}`)} />}
        {tab === 'referrals' && <ReferralsTab referrals={referrals} onRowClick={(r) => navigate(`/referrals/${r.id}`)} />}
        {tab === 'programs' && <ProgramsTab enrollments={enrollments} onRowClick={(e) => navigate(`/enrollments/${e.id}`)} />}
        {tab === 'sessions' && <SessionsTab sessions={sessions} onRowClick={(s) => navigate(`/program-sessions/${s.id}`)} />}
        {tab === 'people' && <PeopleTab people={contacts} onRowClick={(p) => navigate(`/contacts/${p.id}`)} />}
        {tab === 'notes' && <EntityNotesPanel entityType="clients" entityId={client.id} />}
        {tab === 'calendar' && <BusinessCalendarTab clientId={client.id} clientName={client.name} />}
        {tab === 'documents' && <DocumentsTab clientId={client.id} />}
        {tab === 'activity' && <EntityActivityFeed entityType="clients" entityId={client.id} />}
        {tab === 'custom' && <CustomFieldsPanel entityType="clients" entityId={client.id} />}
      </div>

      <CreateEntityModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        table="clients"
        recordId={client.id}
        // The two staff-owner FK fields render as staff pickers (not raw id
        // inputs) in the edit form.
        fkOptions={{ client_manager_user_id: staffOptions, admin_associate_user_id: staffOptions }}
        // ['client-view', id] refreshes THIS page's collapsed BFF payload after an
        // edit; ['client', id] keeps the breadcrumb (useBreadcrumbs) fresh too.
        invalidateKeys={[['clients-list'], ['client', client.id], ['client-view', client.id]]}
      />
    </>
  );
}

// latestAttr / humanizeToken / registryOptions / AttrMap live in
// lib/inlineAttribution (shared by every profile overview).

// ── The Supplier Journey ─────────────────────────────────────────────────────

const JOURNEY_STAGES = [
  { key: 'assess', label: 'Assess' },
  { key: 'connect', label: 'Connect' },
  { key: 'develop', label: 'Develop' },
  { key: 'validate', label: 'Validate' },
  { key: 'procure', label: 'Procure' },
];

/**
 * Where this business has actually got to, derived ONLY from records on file —
 * never stored, never guessed:
 *   Assess   — an assessment exists
 *   Connect  — a referral to a partner exists
 *   Develop  — a program enrollment exists
 *   Validate — a second (reassessment) run exists
 *   Procure  — buyer matching is not built, so this stage never lights
 * Returns '' when nothing is on file, which leaves every stage muted rather
 * than falsely lighting the first one.
 */
function journeyStage(
  assessments: AssessmentRow[],
  referrals: ReferralRow[],
  enrollments: EnrollmentRow[],
): string {
  const reassessed = assessments.length > 1
    || assessments.some((a) => a.sequence && a.sequence !== 'baseline');
  if (reassessed) return 'validate';
  if (enrollments.length > 0) return 'develop';
  if (referrals.length > 0) return 'connect';
  if (assessments.length > 0) return 'assess';
  return '';
}

function OverviewTab({
  client, assessments, referrals, enrollments, staffOptions, staffName,
}: {
  client: Business;
  assessments: AssessmentRow[];
  referrals: ReferralRow[];
  enrollments: EnrollmentRow[];
  staffOptions: { value: string; label: string }[];
  staffName: (uid: string | null) => string;
}) {
  // Parent-organization linkage — when this business rolls up under a parent
  // (clients.parent_client_id), surface it here and link to the parent profile.
  const parentQuery = useQuery<{ id: string; name: string | null }>({
    queryKey: ['parent-client', client.parent_client_id],
    queryFn: ({ signal }) => api.get<{ id: string; name: string | null }>(`/api/parent-clients/${client.parent_client_id}`, signal),
    enabled: !!client.parent_client_id,
  });

  // Per-FIELD attribution (who last changed each field + when). Each box below
  // derives its OWN "last updated by · when" from its fields, so editing
  // Identity never restamps Location/Relationship.
  const attrQuery = useQuery<AttrMap>({
    queryKey: ['field-attribution', 'clients', client.id],
    queryFn: ({ signal }) => api.get<AttrMap>(`/api/field-attribution/clients/${client.id}`, signal),
  });
  const attr = attrQuery.data ?? {};
  const record = client as unknown as Record<string, unknown>;
  const invalidate: unknown[][] = [['client-view', client.id], ['client', client.id], ['field-attribution', 'clients', client.id]];
  // Inline-edit cards default to editable; gate on can_edit so a view-only role
  // can't edit. Hook — before the return.
  const canEditClient = useEntityPermission('client', 'can_edit');

  // Account type + Industry are org-configurable dropdowns (Org Settings registries).
  const clientTypes = useQuery<string[] | { values: string[] }>({ queryKey: ['org-settings', 'client-types'], queryFn: ({ signal }) => api.get('/api/org-settings/client-types', signal), staleTime: 5 * 60 * 1000 });
  const clientCategories = useQuery<string[] | { values: string[] }>({ queryKey: ['org-settings', 'client-categories'], queryFn: ({ signal }) => api.get('/api/org-settings/client-categories', signal), staleTime: 5 * 60 * 1000 });

  const stage = journeyStage(assessments, referrals, enrollments);
  const latest = assessments[0] ?? null;
  const baseline = assessments.length > 1 ? assessments[assessments.length - 1] : null;

  return (
    <>
      {client.parent_client_id && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 'var(--sp-2)',
          padding: 'var(--sp-2) var(--sp-3)', marginBottom: 'var(--sp-4)',
          background: 'var(--brand-primary-bg)', border: '1px solid var(--brand-primary)',
          borderRadius: 'var(--r-lg)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink)',
        }}>
          <span>
            Part of{' '}
            <Link to={`/parent-clients/${client.parent_client_id}`} style={{ color: 'var(--brand-primary)', fontWeight: 'var(--fw-medium)' }}>
              {parentQuery.data?.name ?? 'its parent organization'}
            </Link>
          </span>
        </div>
      )}

      <section style={{
        background: 'var(--color-card)', border: '1px solid var(--color-border)',
        borderRadius: 'var(--r-lg)', padding: 'var(--sp-4)', marginBottom: 'var(--sp-4)',
      }}>
        <SectionHeading
          title="Supplier journey"
          caption="Derived from the records on file: an assessment, a partner referral, a program enrollment, a reassessment. Buyer matching is not built yet, so Procure is never reached."
        />
        <LifecycleBar stages={JOURNEY_STAGES} current={stage} />
        {stage === '' && (
          <p style={{ margin: 'var(--sp-2) 0 0', fontSize: 'var(--fs-sm)', color: 'var(--color-muted)' }}>
            Nothing on file yet — this business has not been assessed, referred or enrolled.
          </p>
        )}
        {latest && (
          <p style={{ margin: 'var(--sp-3) 0 0', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)' }}>
            Latest readiness score <strong>{scoreText(latest.composite_score)}</strong>
            {latest.instrument ? <> on {latest.instrument}{latest.template_version != null ? ` v${latest.template_version}` : ''}</> : null}
            {latest.submitted_at ? <>, submitted {formatDate(latest.submitted_at, { dateOnly: true })}</> : null}
            {baseline && baseline.composite_score != null && latest.composite_score != null && (
              <> — {scoreDelta(baseline.composite_score, latest.composite_score)} since baseline</>
            )}
            .
          </p>
        )}
      </section>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
        gap: 'var(--sp-4)',
      }}>
        <InlineEditCard
          title="Identity"
          routePath="clients"
          recordId={client.id}
          record={record}
          canEdit={canEditClient}
          invalidateKeys={invalidate}
          attribution={latestAttr(attr, ['name', 'short_name', 'type', 'category'])}
          fields={[
            { key: 'name', label: 'Business name', required: true },
            { key: 'short_name', label: 'Short name', required: true },
            { key: 'category', label: 'Industry', type: 'select', options: registryOptions(clientCategories.data), addOptionSlug: 'client-categories', display: (v) => (v ? humanizeToken(String(v)) : '—') },
            { key: 'type', label: 'Account type', type: 'select', options: registryOptions(clientTypes.data), addOptionSlug: 'client-types', display: (v) => (v ? humanizeToken(String(v)) : '—') },
          ]}
        />
        <InlineEditCard
          title="Location"
          routePath="clients"
          recordId={client.id}
          record={record}
          canEdit={canEditClient}
          invalidateKeys={invalidate}
          attribution={latestAttr(attr, ['address_line1', 'city', 'state', 'postal_code'])}
          fields={[
            { key: 'address_line1', label: 'Address' },
            { key: 'city', label: 'City' },
            { key: 'state', label: 'State' },
            { key: 'postal_code', label: 'ZIP' },
          ]}
        />
        <InlineEditCard
          title="Contact & ownership"
          routePath="clients"
          recordId={client.id}
          record={record}
          canEdit={canEditClient}
          invalidateKeys={invalidate}
          attribution={latestAttr(attr, ['website_url', 'fax', 'client_manager_user_id', 'admin_associate_user_id'])}
          fields={[
            {
              key: 'website_url', label: 'Website', type: 'url',
              display: (v) => (v ? <a href={String(v)} target="_blank" rel="noreferrer" style={{ color: 'var(--brand-primary)' }}>{String(v)}</a> : '—'),
            },
            { key: 'fax', label: 'Phone', type: 'phone' },
            { key: 'client_manager_user_id', label: 'Case manager', type: 'select', options: staffOptions, display: (v) => staffName(v as string | null) },
            { key: 'admin_associate_user_id', label: 'Program associate', type: 'select', options: staffOptions, display: (v) => staffName(v as string | null) },
          ]}
        />
      </div>
    </>
  );
}

/** "+4.2" / "-1.5" / "no change" between a baseline and the latest score. */
function scoreDelta(from: number | string, to: number | string): string {
  const a = Number(from);
  const b = Number(to);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 'no comparable baseline';
  const d = b - a;
  if (Math.abs(d) < 0.05) return 'no change';
  return `${d > 0 ? '+' : '−'}${Math.abs(d).toFixed(1)}`;
}

// ── Journey tabs ─────────────────────────────────────────────────────────────

/**
 * A collapsible section that holds closed / finished rows beneath the active
 * list, so a long history never buries what is live.
 */
function PastSection({ label, count, children }: { label: string; count: number; children: React.ReactNode }) {
  return (
    <details style={{ marginTop: 'var(--sp-5)' }}>
      <summary style={{ cursor: 'pointer', color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', padding: 'var(--sp-2) 0' }}>
        <IconArchive size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} /> {label} ({count})
      </summary>
      <div style={{ marginTop: 'var(--sp-3)' }}>{children}</div>
    </details>
  );
}

/**
 * AssessmentsTab — every run of an instrument against this business, newest
 * first. The score is derived server-side and carries the instrument version
 * that produced it; a score you cannot tie to its questions is not comparable.
 */
function AssessmentsTab({ assessments, onRowClick }: { assessments: AssessmentRow[]; onRowClick: (a: AssessmentRow) => void }) {
  const columns = useMemo<ColumnDef<AssessmentRow>[]>(() => [
    { accessorKey: 'instrument', header: 'Instrument', cell: ({ row }) => (
      <div>
        <div style={{ fontWeight: 'var(--fw-medium)' }}>{row.original.instrument || '—'}</div>
        {row.original.template_version != null && (
          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>v{row.original.template_version}</div>
        )}
      </div>
    ) },
    { accessorKey: 'sequence', header: 'Stage', cell: ({ getValue }) => <Badge variant="neutral">{tokenText(getValue() as string)}</Badge> },
    { accessorKey: 'status', header: 'Status', cell: ({ getValue }) => tokenText(getValue() as string) },
    { accessorKey: 'composite_score', header: 'Readiness score', cell: ({ getValue }) => (
      <span style={{ fontWeight: 'var(--fw-semibold)' }}>{scoreText(getValue() as number | null)}</span>
    ) },
    { accessorKey: 'confidence', header: 'Confidence', cell: ({ getValue }) => scoreText(getValue() as number | null) },
    { accessorKey: 'submitted_at', header: 'Submitted', cell: ({ getValue }) => formatDate(getValue() as string | null, { dateOnly: true }) },
    { accessorKey: 'scored_at', header: 'Scored', cell: ({ getValue }) => formatDate(getValue() as string | null, { dateOnly: true }) },
  ], []);

  if (assessments.length === 0) {
    return <Empty message="This business has not been assessed yet. An assessment scores it against a versioned instrument and produces the gaps its growth plan is built from." />;
  }
  return <DataTable data={assessments} columns={columns} onRowClick={onRowClick} />;
}

/**
 * ReferralsTab — every hand-off to an ecosystem partner, and the capability it
 * was for. The capability is the join key: a failed criterion produces a gap, a
 * partner declares a capability, a referral joins them.
 */
function ReferralsTab({ referrals, onRowClick }: { referrals: ReferralRow[]; onRowClick: (r: ReferralRow) => void }) {
  const columns = useMemo<ColumnDef<ReferralRow>[]>(() => [
    { accessorKey: 'capability', header: 'Need', cell: ({ getValue }) => {
      const v = getValue() as string | null;
      return v ? <Badge variant="info">{v}</Badge> : <span style={{ color: 'var(--color-muted)' }}>Not recorded</span>;
    } },
    { accessorKey: 'partner', header: 'Referred to', cell: ({ getValue }) => (getValue() as string) || '—' },
    { accessorKey: 'status', header: 'Status', cell: ({ getValue }) => <Badge variant="neutral">{tokenText(getValue() as string)}</Badge> },
    { accessorKey: 'outcome', header: 'Outcome', cell: ({ getValue }) => tokenText(getValue() as string) },
    { accessorKey: 'opened_date', header: 'Opened', cell: ({ getValue }) => formatDate(getValue() as string | null, { dateOnly: true }) },
    { accessorKey: 'closed_date', header: 'Closed', cell: ({ getValue }) => formatDate(getValue() as string | null, { dateOnly: true }) },
  ], []);

  if (referrals.length === 0) {
    return <Empty message="No referrals for this business yet. A referral routes one capability gap to a partner who offers it." />;
  }
  const open = referrals.filter((r) => !r.closed_date);
  const closed = referrals.filter((r) => r.closed_date);
  return (
    <>
      {open.length === 0
        ? <Empty message="No open referrals — every referral for this business has been closed." />
        : <DataTable data={open} columns={columns} onRowClick={onRowClick} />}
      {closed.length > 0 && (
        <PastSection label="Closed referrals" count={closed.length}>
          <DataTable data={closed} columns={columns} onRowClick={onRowClick} />
        </PastSection>
      )}
    </>
  );
}

/** ProgramsTab — this business's development-program enrollments and cohorts. */
function ProgramsTab({ enrollments, onRowClick }: { enrollments: EnrollmentRow[]; onRowClick: (e: EnrollmentRow) => void }) {
  const columns = useMemo<ColumnDef<EnrollmentRow>[]>(() => [
    { accessorKey: 'program', header: 'Program', cell: ({ getValue }) => (getValue() as string) || '—' },
    { accessorKey: 'cohort', header: 'Cohort', cell: ({ getValue }) => (getValue() as string) || '—' },
    { accessorKey: 'stage', header: 'Stage', cell: ({ getValue }) => <Badge variant="neutral">{tokenText(getValue() as string)}</Badge> },
    { accessorKey: 'role', header: 'Role', cell: ({ getValue }) => tokenText(getValue() as string) },
    { accessorKey: 'application_date', header: 'Applied', cell: ({ getValue }) => formatDate(getValue() as string | null, { dateOnly: true }) },
    { accessorKey: 'start_date', header: 'Start', cell: ({ getValue }) => formatDate(getValue() as string | null, { dateOnly: true }) },
    { accessorKey: 'end_date', header: 'End', cell: ({ getValue }) => formatDate(getValue() as string | null, { dateOnly: true }) },
    // attendance_rate carries no documented unit, so it is shown as stored
    // rather than dressed up as a percentage it may not be.
    { accessorKey: 'attendance_rate', header: 'Attendance', cell: ({ getValue }) => scoreText(getValue() as number | null) },
  ], []);

  if (enrollments.length === 0) {
    return <Empty message="This business is not enrolled in any program yet." />;
  }
  const current = enrollments.filter((e) => !e.end_date);
  const finished = enrollments.filter((e) => e.end_date);
  return (
    <>
      {current.length === 0
        ? <Empty message="No current enrollments — every program this business joined has ended." />
        : <DataTable data={current} columns={columns} onRowClick={onRowClick} />}
      {finished.length > 0 && (
        <PastSection label="Finished enrollments" count={finished.length}>
          <DataTable data={finished} columns={columns} onRowClick={onRowClick} />
        </PastSection>
      )}
    </>
  );
}

/** SessionsTab — the coaching and consultation sessions delivered, newest first. */
function SessionsTab({ sessions, onRowClick }: { sessions: SessionRow[]; onRowClick: (s: SessionRow) => void }) {
  const columns = useMemo<ColumnDef<SessionRow>[]>(() => [
    // What was delivered leads; the session's own title is a legacy booking
    // reference ("T-0352") in the migrated data, so it reads as the sub-line.
    { id: 'what', header: 'Session', cell: ({ row }) => (
      <div>
        <div style={{ fontWeight: 'var(--fw-medium)' }}>{row.original.service || row.original.title || '—'}</div>
        {row.original.service && row.original.title && (
          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>{row.original.title}</div>
        )}
      </div>
    ) },
    { accessorKey: 'kind', header: 'Kind', cell: ({ getValue }) => <Badge variant="neutral">{tokenText(getValue() as string)}</Badge> },
    { accessorKey: 'starts_at', header: 'When', cell: ({ getValue }) => {
      const v = getValue() as string | null;
      return v ? formatDateTime(v) : '—';
    } },
    { accessorKey: 'mode', header: 'Mode', cell: ({ getValue }) => tokenText(getValue() as string) },
    { accessorKey: 'location', header: 'Location', cell: ({ getValue }) => (getValue() as string) || '—' },
    { accessorKey: 'status', header: 'Status', cell: ({ getValue }) => tokenText(getValue() as string) },
  ], []);

  if (sessions.length === 0) {
    return <Empty message="No coaching sessions recorded for this business yet." />;
  }
  return <DataTable data={sessions} columns={columns} onRowClick={onRowClick} />;
}

/**
 * PeopleTab — the people at this business, from contact_list. Rendered off the
 * BFF payload (one round-trip) rather than a second read; each row opens that
 * person's own profile, where their email addresses and phone numbers live.
 */
function PeopleTab({ people, onRowClick }: { people: Person[]; onRowClick: (p: Person) => void }) {
  const columns = useMemo<ColumnDef<Person>[]>(() => [
    { id: 'name', header: 'Name', cell: ({ row }) => (
      <span style={{ fontWeight: 'var(--fw-medium)' }}>
        {row.original.search_name || [row.original.first_name, row.original.last_name].filter(Boolean).join(' ') || row.original.company_name || 'Unnamed person'}
      </span>
    ) },
    { accessorKey: 'role', header: 'Role at the business', cell: ({ getValue }) => (getValue() as string) || '—' },
    { accessorKey: 'title', header: 'Job title', cell: ({ getValue }) => (getValue() as string) || '—' },
    { accessorKey: 'status', header: 'Status', cell: ({ getValue }) => <Badge variant="neutral">{tokenText(getValue() as string)}</Badge> },
  ], []);

  if (people.length === 0) {
    return <Empty message="No people are on file at this business yet." />;
  }
  return <DataTable data={people} columns={columns} onRowClick={onRowClick} />;
}

// ── Calendar ─────────────────────────────────────────────────────────────────

/**
 * BusinessCalendarTab — the per-business Calendar staff use to manage this
 * business's events. Loads GET /api/calendar/events?client_id=<id> (server-side
 * org + row scoped — the same predicate the org calendar uses), and splits into
 * Upcoming / Past. "+ New event" and every row open the SAME shared EventModal,
 * PRE-LINKED to this business. Create / edit / delete happen inline.
 */
function BusinessCalendarTab({ clientId, clientName }: { clientId: string; clientName: string }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const canView = useEntityPermission('event', 'can_view');
  const canAdd = useEntityPermission('event', 'can_add');
  const canEdit = useEntityPermission('event', 'can_edit');
  const canDelete = useEntityPermission('event', 'can_delete');

  const [editing, setEditing] = useState<CalendarEvent | null>(null);
  const [creating, setCreating] = useState(false);

  const eventsQuery = useQuery<CalendarEvent[]>({
    queryKey: ['client-calendar-events', clientId],
    queryFn: ({ signal }) => api.get<CalendarEvent[]>(`/api/calendar/events?client_id=${clientId}`, signal),
    enabled: canView && !!clientId,
  });
  const usersQuery = useQuery<UserLite[]>({
    queryKey: ['calendar-users-list'],
    queryFn: ({ signal }) => api.get<UserLite[]>('/api/users-list', signal),
    enabled: canView,
  });

  // Events created/edited here also appear on the main Calendar (['calendar-events'])
  // and the Dashboard "Upcoming Events" box (['calendar-upcoming']) — refresh all
  // three so those surfaces don't lag behind this tab (symmetric to CalendarPage).
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['client-calendar-events', clientId] });
    queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
    queryClient.invalidateQueries({ queryKey: ['calendar-upcoming'] });
  };
  const createMut = useMutation({
    mutationFn: (body: Partial<CalendarEvent>) => api.post<CalendarEvent>('/api/calendar/events', body),
    onSuccess: () => { invalidate(); setCreating(false); toast({ variant: 'success', message: 'Event created.' }); },
    onError: (err) => toast({ variant: 'error', message: err instanceof ApiClientError ? err.message : 'Could not create the event.' }),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<CalendarEvent> }) => api.put<CalendarEvent>(`/api/calendar/events/${id}`, body),
    onSuccess: () => { invalidate(); setEditing(null); toast({ variant: 'success', message: 'Event updated.' }); },
    onError: (err) => toast({ variant: 'error', message: err instanceof ApiClientError ? err.message : 'Could not update the event.' }),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => api.delete(`/api/calendar/events/${id}`),
    onSuccess: () => { invalidate(); setEditing(null); toast({ variant: 'success', message: 'Event deleted.' }); },
    onError: (err) => toast({ variant: 'error', message: err instanceof ApiClientError ? err.message : 'Could not delete the event.' }),
  });

  const events = eventsQuery.data ?? [];
  const today = todayIso();
  const upcoming = useMemo(
    () => events.filter((e) => e.event_date >= today)
      .sort((a, b) => (a.event_date === b.event_date
        ? (a.event_time ?? '').localeCompare(b.event_time ?? '')
        : a.event_date.localeCompare(b.event_date))),
    [events, today],
  );
  const past = useMemo(
    () => events.filter((e) => e.event_date < today)
      .sort((a, b) => (a.event_date === b.event_date
        ? (b.event_time ?? '').localeCompare(a.event_time ?? '')
        : b.event_date.localeCompare(a.event_date))),
    [events, today],
  );

  if (!canView) {
    return <Empty message="You do not have permission to view this business's calendar." />;
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--sp-3)', marginBottom: 'var(--sp-4)', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 'var(--sp-4)', flexWrap: 'wrap' }}>
          <StatCard label="Upcoming" value={String(upcoming.length)} caption="on this business" accent="hunter" />
          <StatCard label="Past" value={String(past.length)} caption="recorded" accent="info" />
        </div>
        {canAdd && <Button variant="primary" size="sm" onClick={() => setCreating(true)}>+ New event</Button>}
      </div>

      {eventsQuery.isLoading && <p style={{ color: 'var(--color-ink-3)' }}>Loading events…</p>}
      {eventsQuery.error && (
        <div style={{ padding: 'var(--sp-4)', background: 'var(--color-status-red-bg)', border: '1px solid var(--color-status-red)', color: 'var(--color-status-red)', borderRadius: 'var(--r-lg)' }}>
          {(eventsQuery.error as Error).message}
        </div>
      )}
      {!eventsQuery.isLoading && !eventsQuery.error && events.length === 0 && (
        <Empty message="No events on this business yet. Use “+ New event” to schedule a coaching session, a check-in, or a reminder." />
      )}

      {upcoming.length > 0 && (
        <BusinessEventSection title="Upcoming" events={upcoming} users={usersQuery.data ?? []} onOpen={setEditing} />
      )}
      {past.length > 0 && (
        <BusinessEventSection title="Past" events={past} users={usersQuery.data ?? []} onOpen={setEditing} />
      )}

      {(creating || editing) && (
        <EventModal
          key={editing?.id ?? 'new'}
          event={editing}
          defaultDate={todayIso()}
          users={usersQuery.data ?? []}
          // There is no property/parcel chain in this product — the event links
          // to the business and nothing else. (`parcels` was removed from the
          // modal's props entirely when the calendar was repointed.)
          clients={[]}
          lockClient={{ id: clientId, name: clientName }}
          canEdit={editing ? canEdit : canAdd}
          canDelete={canDelete}
          saving={createMut.isPending || updateMut.isPending}
          deleting={deleteMut.isPending}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSubmit={(body) => {
            if (editing) updateMut.mutate({ id: editing.id, body });
            else createMut.mutate(body);
          }}
          onDelete={editing ? () => deleteMut.mutate(editing.id) : undefined}
        />
      )}
    </>
  );
}

/** A titled list of this business's events; each row opens the shared EventModal. */
function BusinessEventSection({
  title, events, users, onOpen,
}: {
  title: string;
  events: CalendarEvent[];
  users: UserLite[];
  onOpen: (e: CalendarEvent) => void;
}) {
  return (
    <section style={{ marginBottom: 'var(--sp-5)' }}>
      <h3 style={{ margin: '0 0 var(--sp-2)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)' }}>{title}</h3>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
        {events.map((e) => {
          const meta = metaFor(e.event_type);
          return (
            <li key={e.id}>
              <button
                onClick={() => onOpen(e)}
                style={{
                  width: '100%', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
                  background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md, 8px)',
                  padding: 'var(--sp-2) var(--sp-3)', borderLeft: `3px solid ${meta.accent}`,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--sp-2)' }}>
                  <span style={{ fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)', fontSize: 'var(--fs-sm)' }}>
                    {eventTitle(e)}
                  </span>
                  <Badge variant={meta.variant}>{meta.label}</Badge>
                </div>
                <div style={{ marginTop: 2, fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
                  {formatEventDate(e.event_date)}{e.event_time ? ` · ${formatEventTime(e.event_time)}` : ''}{e.location ? ` · ${e.location}` : ''}
                  {e.assigned_user_id ? ` · ${userLabel(users, e.assigned_user_id)}` : ''}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ── Documents ────────────────────────────────────────────────────────────────

interface BusinessDocument {
  id: string;
  name: string;
  document_type: string | null;
  filename: string;
  file_size_bytes: number | null;
  year: number;
  cadence: 'one_time' | 'annual' | null;
  status: string | null;
  created_at: string;
}

const fmtBytes = (v: number | null | undefined) => {
  if (v == null) return '—';
  if (v < 1024) return `${v}B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)}KB`;
  return `${(v / 1024 / 1024).toFixed(1)}MB`;
};

const DOCS_PAGE_SIZE = 20;

/**
 * DocumentsTab — every document housed on this business (GET
 * /api/documents?client_id=), with the list-page toolbar + pager pattern.
 * Upload reuses the shared DocumentUploadModal, pre-linked to this business.
 */
function DocumentsTab({ clientId }: { clientId: string }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [uploadOpen, setUploadOpen] = useState(false);

  // Client-side search / type-filter / pagination over the already-fetched
  // documents (the endpoint returns every doc linked to the business). A filter
  // or search change resets to page 1.
  const [docSearch, setDocSearch] = useState('');
  const [docType, setDocType] = useState('');
  const [docPage, setDocPage] = useState(0); // 0-based page index

  // Keyed under ['documents', …] so the shared DocumentUploadModal's onSuccess
  // invalidation of ['documents'] refreshes this list automatically.
  const docsQuery = useQuery<BusinessDocument[]>({
    queryKey: ['documents', 'client', clientId],
    queryFn: ({ signal }) => api.get<BusinessDocument[]>(`/api/documents?client_id=${clientId}`, signal),
    enabled: !!clientId,
  });

  const docColumns = useMemo<ColumnDef<BusinessDocument>[]>(() => [
    { accessorKey: 'name', header: 'Name' },
    { accessorKey: 'document_type', header: 'Type', cell: ({ getValue }) => {
      const v = getValue() as string | null; return v ? <Badge variant="neutral">{v}</Badge> : '—';
    } },
    { accessorKey: 'year', header: 'Year' },
    { accessorKey: 'file_size_bytes', header: 'Size', cell: ({ getValue }) => fmtBytes(getValue() as number | null) },
    { accessorKey: 'created_at', header: 'Uploaded', cell: ({ getValue }) => formatDate(getValue() as string | null, { dateOnly: true }) },
    { accessorKey: 'status', header: 'Status', cell: ({ getValue }) => <Badge variant="neutral">{(getValue() as string) || 'Received'}</Badge> },
    { id: 'download', header: '', cell: ({ row }) => (
      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); downloadDocument(row.original.id, (m) => toast({ variant: 'error', message: m })); }}>Download</Button>
    ) },
  ], [toast]);

  const docs = docsQuery.data ?? [];

  // Distinct document types present on this business → the type-filter Select.
  const docTypeOptions = useMemo<SelectOption[]>(() => {
    const seen = new Set<string>();
    for (const d of docs) if (d.document_type) seen.add(d.document_type);
    return [...seen].sort().map((t) => ({ value: t, label: t }));
  }, [docs]);

  // Search matches name + type substring; the type filter is exact.
  const filteredDocs = useMemo(() => {
    const q = docSearch.trim().toLowerCase();
    return docs.filter((d) => {
      if (docType && (d.document_type ?? '') !== docType) return false;
      if (!q) return true;
      return `${d.name ?? ''} ${d.document_type ?? ''}`.toLowerCase().includes(q);
    });
  }, [docs, docSearch, docType]);

  const docPageCount = Math.max(1, Math.ceil(filteredDocs.length / DOCS_PAGE_SIZE));
  // Clamp the page so shrinking the result set (via search/filter) never lands on
  // an out-of-range empty page even before the reset handlers run.
  const docSafePage = Math.min(docPage, docPageCount - 1);
  const pagedDocs = useMemo(
    () => filteredDocs.slice(docSafePage * DOCS_PAGE_SIZE, docSafePage * DOCS_PAGE_SIZE + DOCS_PAGE_SIZE),
    [filteredDocs, docSafePage],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="primary" size="sm" onClick={() => setUploadOpen(true)}>Upload document</Button>
      </div>

      <section>
        <SectionHeading title="All documents" caption="Every document linked to this business." />
        {docsQuery.isLoading ? (
          <p style={{ color: 'var(--color-ink-3)' }}>Loading documents…</p>
        ) : docsQuery.error ? (
          <ErrorBox error={docsQuery.error as Error} />
        ) : docs.length === 0 ? (
          <Empty message="No documents on this business yet. Use “Upload document” to add one." />
        ) : (
          <>
            <ListToolbar
              searchValue={docSearch}
              onSearchChange={(v) => { setDocSearch(v); setDocPage(0); }}
              searchPlaceholder="Search documents by name or type…"
              filters={[
                {
                  key: 'document_type',
                  value: docType,
                  onChange: (v) => { setDocType(v); setDocPage(0); },
                  placeholder: 'All types',
                  options: docTypeOptions,
                  widthPx: 200,
                },
              ]}
            />
            <DataTable
              data={pagedDocs}
              columns={docColumns}
              onRowClick={(d) => navigate(`/documents/${d.id}`)}
              emptyMessage="No documents match your search."
            />
            {filteredDocs.length > DOCS_PAGE_SIZE && (
              <Pagination
                pageIndex={docSafePage}
                pageCount={docPageCount}
                pageSize={DOCS_PAGE_SIZE}
                totalRows={filteredDocs.length}
                onPageChange={setDocPage}
              />
            )}
          </>
        )}
      </section>

      <DocumentUploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        defaultContext={{ client_id: clientId }}
      />
    </div>
  );
}

// ── Shared bits ──────────────────────────────────────────────────────────────

function SectionHeading({ title, caption }: { title: string; caption?: string }) {
  return (
    <div style={{ marginBottom: 'var(--sp-3)' }}>
      <h3 style={{ margin: 0, fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)' }}>{title}</h3>
      {caption && <p style={{ margin: 'var(--sp-1) 0 0', fontSize: 'var(--fs-xs)', color: 'var(--color-muted)' }}>{caption}</p>}
    </div>
  );
}

function ErrorBox({ error }: { error: Error }) {
  return (
    <div style={{
      padding: 'var(--sp-4)', background: 'var(--color-status-red-bg)',
      border: '1px solid var(--color-status-red)', color: 'var(--color-status-red)',
      borderRadius: 'var(--r-lg)',
    }}>{error.message}</div>
  );
}

function Empty({ message }: { message: string }) {
  return (
    <div style={{
      padding: 'var(--sp-6)',
      background: 'var(--color-card)',
      border: '1px dashed var(--color-border)',
      borderRadius: 'var(--r-lg)',
      color: 'var(--color-muted)',
      textAlign: 'center',
      fontSize: 'var(--fs-md)',
    }}>{message}</div>
  );
}
